import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInfoCircle, FaCheck, FaTimes, FaFilter, FaSort, FaExpand, FaVideo } from 'react-icons/fa';
import {
  getAllOrdersAsync,
  acceptOrderAsync,
  rejectOrderAsync,
  fetchOrderByIdAsync,
  clearSelectedOrder,
  setFilterUsername
} from '../features/order/orderSlice';
import { fetchProductAsync } from '../features/products/productsSlice';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import Pagination from '../features/components/Pagination';
import { toast } from 'react-toastify';
import Footer from '../features/components/Footer';

const ITEMS_PER_PAGE = 16;

const ProductDetailsDialog = ({ isOpen, onClose, product }) => {
  const [selectedMedia, setSelectedMedia] = useState('cover_image');

  if (!isOpen || !product) return null;

  const {
    name,
    weight,
    purity,
    making_charge: making,
    cover_image,
    image2,
    image3,
    video,
    quantity,
    registered_firm_name: registeredFirmName,
    description,
    category,
    owner_name: ownerName
  } = product;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Media Section */}
            <div className="space-y-4">
              {/* Main Media Display */}
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
                {selectedMedia === 'video' && video ? (
                  <video
                    src={video}
                    controls
                    className="h-full w-full object-cover object-center"
                  />
                ) : (
                  <img
                    src={selectedMedia === 'cover_image' ? cover_image :
                        selectedMedia === 'image2' ? image2 :
                        selectedMedia === 'image3' ? image3 : cover_image}
                    alt={name}
                    className="h-full w-full object-cover object-center"
                  />
                )}
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-4 gap-2">
                <div
                  className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                    selectedMedia === 'cover_image' ? 'ring-2 ring-yellow-500' : ''
                  }`}
                  onClick={() => setSelectedMedia('cover_image')}
                >
                  <img
                    src={cover_image}
                    alt="Cover"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                
                {image2 && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'image2' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('image2')}
                  >
                    <img
                      src={image2}
                      alt="Image 2"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )}

                {image3 && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer ${
                      selectedMedia === 'image3' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('image3')}
                  >
                    <img
                      src={image3}
                      alt="Image 3"
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )}

                {video && (
                  <div
                    className={`aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200 cursor-pointer relative ${
                      selectedMedia === 'video' ? 'ring-2 ring-yellow-500' : ''
                    }`}
                    onClick={() => setSelectedMedia('video')}
                  >
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                      <FaVideo className="text-white text-3xl" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {weight && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Weight</h3>
                      <p className="mt-1 text-sm text-gray-900">{weight} g</p>
                    </div>
                  )}
                  {purity && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Purity</h3>
                      <p className="mt-1 text-sm text-gray-900">{purity}K</p>
                    </div>
                  )}
                  {making && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Making Charge</h3>
                      <p className="mt-1 text-sm text-gray-900">{making}%</p>
                    </div>
                  )}
                  {quantity && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Available Quantity</h3>
                      <p className="mt-1 text-sm text-gray-900">{quantity}</p>
                    </div>
                  )}
                </div>

                {category && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Category</h3>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{category}</p>
                  </div>
                )}

                {description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Description</h3>
                    <p className="mt-1 text-sm text-gray-900">{description}</p>
                  </div>
                )}

                {(ownerName || registeredFirmName) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Seller Details</h3>
                    <div className="mt-2 space-y-2">
                      {ownerName && (
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">Name:</span> {ownerName}
                        </p>
                      )}
                      {registeredFirmName && (
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">Firm:</span> {registeredFirmName}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderItem = ({ order, onViewDetails, onAccept, onReject, orderType }) => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  return (
    <tr>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img
              src={order.cover_image}
              alt={order.product_name}
              className={`h-12 w-12 object-cover rounded-md cursor-pointer transition-all duration-300 ${
                isImageExpanded ? 'h-48 w-48' : ''
              }`}
              onClick={() => setIsImageExpanded(!isImageExpanded)}
            />
            <FaExpand className="absolute bottom-1 right-1 text-gray-500 text-xs" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{order.product_name}</div>
            <div className="text-sm text-gray-500">ID: {order.product_id}</div>
          </div>
        </div>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {order.quantity}
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{order.registered_firm_name}</div>
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(order.order_date).toLocaleDateString()}
      </td>
      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => onViewDetails(order.product_id)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <FaInfoCircle className="h-5 w-5" />
          </button>
          {orderType === 'PENDING' && (
            <>
              <button
                onClick={() => onAccept(order.order_id)}
                className="text-green-600 hover:text-green-900"
                title="Accept Order"
              >
                <FaCheck className="h-5 w-5" />
              </button>
              <button
                onClick={() => onReject(order.order_id)}
                className="text-red-600 hover:text-red-900"
                title="Reject Order"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const WholesalerOrders = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [firmFilter, setFirmFilter] = useState('');
  const navigate = useNavigate();

  const { pendingOrders, approvedOrders, loading, error } = useSelector(state => state.orders);
  const { currentProduct } = useSelector(state => state.products);
  
  const orderType = location.pathname.includes('requests') ? 'PENDING' : 'APPROVED';
  const orders = orderType === 'PENDING' ? pendingOrders : approvedOrders;

  useEffect(() => {
    dispatch(getAllOrdersAsync(orderType));
  }, [dispatch, orderType]);

  useEffect(() => {
    if (selectedProductId) {
      dispatch(fetchProductAsync(selectedProductId));
    }
  }, [dispatch, selectedProductId]);

  const handleAccept = async (orderId) => {
    try {
      await dispatch(acceptOrderAsync(orderId)).unwrap();
      toast.success('Order accepted successfully');
      dispatch(getAllOrdersAsync('PENDING')); // Refresh pending orders
    } catch (error) {
      toast.error(error.message || 'Failed to accept order');
    }
  };

  const handleReject = async (orderId) => {
    try {
      await dispatch(rejectOrderAsync(orderId)).unwrap();
      toast.success('Order rejected successfully');
      dispatch(getAllOrdersAsync('PENDING')); // Refresh pending orders
    } catch (error) {
      toast.error(error.message || 'Failed to reject order');
    }
  };

  const handleViewDetails = (productId) => {
    setSelectedProductId(productId);
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Filter and sort orders
  const filteredAndSortedOrders = [...orders]
    .filter(order => !firmFilter || order.registered_firm_name.toLowerCase().includes(firmFilter.toLowerCase()))
    .sort((a, b) => {
      const dateA = new Date(a.order_date);
      const dateB = new Date(b.order_date);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  const totalPages = Math.ceil(filteredAndSortedOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredAndSortedOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <WholesalerNavbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {orderType === 'PENDING' ? 'Pending Orders' : 'Approved Orders'}
          </h1>
          <button
            onClick={() => navigate(orderType === 'PENDING' ? '/wholesaler/orders/approved' : '/wholesaler/orders/requests')}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            {orderType === 'PENDING' ? 'View Approved' : 'View Pending'}
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Filter by firm name..."
                value={firmFilter}
                onChange={(e) => setFirmFilter(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
              />
              <FaFilter className="absolute left-3 top-3 text-gray-400" />
            </div>
            <button
              onClick={toggleSortOrder}
              className="mt-4 sm:mt-0 flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <FaSort className={`mr-2 transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
              Sort by Date
            </button>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 p-4 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && filteredAndSortedOrders.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No {orderType.toLowerCase()} orders found</p>
            </div>
          )}

          {!loading && !error && filteredAndSortedOrders.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Details
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Firm Name
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Date
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedOrders.map((order) => (
                      <OrderItem
                        key={order.order_id}
                        order={order}
                        onViewDetails={handleViewDetails}
                        onAccept={handleAccept}
                        onReject={handleReject}
                        orderType={orderType}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <ProductDetailsDialog
        isOpen={!!selectedProductId}
        onClose={() => setSelectedProductId(null)}
        product={currentProduct}
      />

      <Footer />
    </div>
  );
};

export default WholesalerOrders;